import * as React from 'react';

import { useQuery } from '@apollo/client';

import { ColorCircle, Text } from '@notino/react-styleguide';
import {
  ProductIdType,
  SimpleProductAttributesByIdsQuery,
  SimpleProductAttributesByIdsQueryVariables,
  VariantDisplayType,
} from '@notino/shared/definitions/types';

import { Flex } from '@components/Flex';
import { getVariantName } from '@containers/ProductDetailContainer/ProductDetail/utils';
import { useProductDetailContext } from '@containers/ProductDetailContainer/ProductDetailContext';
import { useDisplayType } from '@containers/ProductDetailContainer/utils';
import { styled } from '@linaria/react';

import simpleProductQuery from './queries/simpleProduct.graphql';

type Props = {
  catalogId: string;
};

const StyledCircle = styled(ColorCircle)`
  min-height: 1rem;
  min-width: 1rem;
  width: 1rem;
  height: 1rem;
`;

export const VariantDisplay = ({ catalogId }: Props) => {
  const { product } = useProductDetailContext();
  const displayType = useDisplayType(product);

  const foundVariant = product?.variants?.find(
    (v) => v.catalogId === catalogId
  );

  const { data: fetchedVariantData } = useQuery<
    SimpleProductAttributesByIdsQuery,
    SimpleProductAttributesByIdsQueryVariables
  >(simpleProductQuery, {
    skip: Boolean(foundVariant),
    variables: {
      idType: ProductIdType.Catalog,
      ids: [catalogId],
    },
  });

  const variant =
    foundVariant || fetchedVariantData?.simpleProductAttributesByIds?.[0];

  if (!variant) {
    return null;
  }

  const variantName = getVariantName(
    variant as Parameters<typeof getVariantName>[0]
  );
  if (!variantName) {
    return null;
  }

  if (displayType === VariantDisplayType.ColorPicker) {
    return (
      <Flex gap="4px" align="center">
        <StyledCircle
          colors={variant.colors as string[]}
          width={1}
          height={1}
        />
        <Text typography="labelSmall400" color="text.secondary">
          {variantName}
        </Text>
      </Flex>
    );
  }
  return (
    <Text typography="labelSmall400" color="text.secondary">
      {variantName}
    </Text>
  );
};
